import {
  PRODUCTS,
  OP_EQUALS,
  TEST_TYPE,
  TYPE_OPTION,
  DEVICE_TYPE,
  TEST_STATUS,
  SOURCE_FIGMA,
  SURVEY_TYPES,
  SOURCE_MARVEL,
  SOURCE_INVISION,
  QUESTION_CATEGORY_RADIO,
  QUESTION_CATEGORY_CHECKBOX,
  TEST_TYPE_TASK,
  TEST_TYPE_NORECORDING,
  TOOLKIT,
  TEST_TYPE_CARDSORT,
  TEST_TYPE_TREETEST,
  TEST_TYPE_MODERATED,
  TEST_TYPE_SURVEY,
  DEEPDIVE_SESSION_STATE,
  TASK_TYPES,
  REPORT_CUSTOM_FILTER_TYPES,
  REPORT_FILTERS_EVENT,
  SUCCESS_CRITERIA,
  GENDER,
  RECORDING_DEVICE,
  RECORDING_BROWSER,
} from "../actions/constants";
import { PATHS } from "./paths";
import { downloadCsv } from "./csv";
import dayjs from "./dayjs";
import moment from "moment";
import parser from "ua-parser-js";
import { Link } from "react-router-dom";
import { userActions } from "../actions/user";
import { isAndroid, isIOS, isChrome, isSafari } from "react-device-detect";

const { RADIO, CHECKBOX, LIKERT_5PT, LIKERT_7PT, LIKERT_10PT, NPS } = SURVEY_TYPES;
const { REACT_APP_BASE_URL, REACT_APP_BASENAME } = process.env;

// Get Yii URL
export const getYiiURL = (path = "") => `${REACT_APP_BASE_URL}/${path}`;

// Get S3 URL
export const getS3URL = (path = "") => `${process.env.REACT_APP_S3_BASEPATH}/${path}`;

// Get Frontend URL
export const getFrontendURL = (path = "") => `${window.location.origin + REACT_APP_BASENAME}/${path}`;

export const getPercent = (value = 0, total = 0, toFix = false, prec = 2) => {
  if (total <= 0) return 0;

  const percent = (value / total) * 100;
  return toFix ? Number(percent.toFixed(prec)) : Math.round(percent);
};

export const getRandomID = (size = 12) => {
  const charset = "abcdefghijklmnopqrstuvwxyz";
  return [...Array(size)].map((_) => charset[Math.floor(Math.random() * charset.length)]).join("");
};

export const getAnswerObj = (value = "") => ({ op: OP_EQUALS, var: [{ type: TYPE_OPTION, value }] });

export const getOption = (position = 0, title = "", type = QUESTION_CATEGORY_RADIO, source_title = "") => ({
  position,
  title,
  source_title,
  attachment: [],
  properties: {},
  id: getRandomID(),
  criteria: type === QUESTION_CATEGORY_CHECKBOX ? "maybe" : "must",
});

export const getQuestion = (position = 0) => ({
  position,
  title: "",
  source_title: "",
  validation: {},
  attachment: [],
  id: getRandomID(),
  options: [getOption()],
  answers: getAnswerObj(),
  category: QUESTION_CATEGORY_RADIO,
  translation: { tool: null, lang: null },
  properties: { optional: false, use_others: false },
});

export const getParam = (location, key) => new URLSearchParams(location.search).get(key);

// Manual Fulfilment links: ops-generated guest links that fill UXArmy panel quota. The "mf_" prefix
// mirrors CustomTrackingLinks::hasMfPrefix(); the server still decides whether a link really is one.
export const isManualFulfilmentLink = (uxarmyUniqueId) =>
  typeof uxarmyUniqueId === "string" && uxarmyUniqueId.toLowerCase().startsWith("mf_");

// Scoped per test so opting to skip preview screens for one test doesn't skip them for every other test.
export const getPreviewSkipStorageKey = (testId) => (testId ? `previewSkip_${testId}` : "previewSkip");

// Convert \n into <br>
export const newLineToBr = (string = "") => string.replace(/\n/g, "<br />");

export const goBackToWebsite = (referrer) =>
  window.location.assign(referrer ? referrer : `${process.env.REACT_APP_SERVER_LOGIN_URL}?returnUrl=${window.location.href}`);

export const getDeviceName = (deviceType) => {
  const deviceNames = {
    [DEVICE_TYPE.TABLET]: "Tablet",
    [DEVICE_TYPE.MOBILE]: "Smartphone",
    [DEVICE_TYPE.COMPUTER]: "Computer (PC)",
    [DEVICE_TYPE.TABLET_IPAD]: "Tablet / iPad",
    [DEVICE_TYPE.TABLET_AND_MOBILE]: "Mobile / Tablet",
  };

  return deviceNames[deviceType] || "Unknown";
};

export const getRecordingTestDeviceName = (deviceType) => {
  const deviceNames = {
    [DEVICE_TYPE.TABLET]: "Tablet",
    [DEVICE_TYPE.MOBILE]: "Smartphone",
    [DEVICE_TYPE.COMPUTER]: "Computer",
    [DEVICE_TYPE.TABLET_IPAD]: "Tablet / iPad",
    [DEVICE_TYPE.TABLET_AND_MOBILE]: "Mobile / Tablet",
    [DEVICE_TYPE.ANY]: "Any",
  };

  return deviceNames[deviceType] || "Unknown";
};

export const getNoRecordingTestDeviceName = (deviceType) => {
  const deviceNames = {
    [DEVICE_TYPE.TABLET]: "Tablet",
    [DEVICE_TYPE.MOBILE]: "Mobile",
    [DEVICE_TYPE.COMPUTER]: "Desktop",
    [DEVICE_TYPE.TABLET_IPAD]: "Tablet / iPad",
    [DEVICE_TYPE.TABLET_AND_MOBILE]: "Mobile / Tablet",
    [DEVICE_TYPE.ANY]: "Any",
  };

  return deviceNames[deviceType] || "Unknown";
};

export const getTestStatus = (status) => {
  const testStatus = {
    [TEST_STATUS.DRAFT]: "Not Activated ",
    [TEST_STATUS.INACTIVE]: "Inactive",
    [TEST_STATUS.ACTIVE]: "Active",
    [TEST_STATUS.COMPLETED]: "Completed",
    [TEST_STATUS.HIDDEN]: "Hidden",
    [TEST_STATUS.DRYRUN]: "Dry Run Mode",
    [TEST_STATUS.TEMP_AB_TEST]: "Dropout",
    [TEST_STATUS.SAVED_TEST]: "Draft",
    [TEST_STATUS.DELETE_SUFFIX]: "Deleted",
    [TEST_STATUS.EDIT_TEST]: "Draft",
    [TEST_STATUS.PREVIEW_TEST]: "Preview",
  };

  return testStatus[status] || "Unknown";
};

export const getTestTyeName = (testType) => {
  const testTypeNames = {
    [TEST_TYPE.MULTIOS_APP]: "iOS/Android App Test",
    [TEST_TYPE.IOS_APP]: "iOS App Test",
    [TEST_TYPE.IOS_SDK]: "iOS SDK Test",
    [TEST_TYPE.WEBSITE]: "Website Test",
    [TEST_TYPE.MODERATED]: "Moderated Test",
    [TEST_TYPE.ANDROID_APP]: "Android App Test",
    [TEST_TYPE.ANDROID_SDK]: "Android SDK Test",
  };

  return testTypeNames[testType] || "Unknown";
};

export const getTestTypeForRecruit = (test, isToolkit = false, isModerated = false) => {
  if (isModerated) {
    return TEST_TYPE_MODERATED;
  } else if (isToolkit) {
    // Toolkit
    if (test?.data?.type === TOOLKIT.TEST_TYPES.cardsort || test?.type === TOOLKIT.TEST_TYPES.cardsort) {
      // Card sort
      return TEST_TYPE_CARDSORT;
    }
    if (test?.data?.type === TOOLKIT.TEST_TYPES.treetest || test?.type === TOOLKIT.TEST_TYPES.treetest) {
      // Tree test
      return TEST_TYPE_TREETEST;
    }
  } else {
    // TBT, Figma no recording, Survey, Moderated
    if (test?.type === TEST_TYPE_TASK && Boolean(Number(test?.is_recording_off))) {
      // Figma no recording test
      return TEST_TYPE_NORECORDING;
    }
    return test?.type;
  }
};

export const isPanelistCountValid = (responsesCollected, users_max, uxarmyCredits, creditsPanel) => {
  const uc = (Number(users_max) - Number(responsesCollected?.panelTesters || 0)) * Number(uxarmyCredits);
  return uc > Number(creditsPanel) ? false : true;
};

export const uxTestersNationalityStringToArray = (ux_testers_nationality) => {
  return ux_testers_nationality
    ? ux_testers_nationality.toString().replaceAll('"', "").replaceAll("'", "").replace(/[[\]]/g, "").split(",")
    : null;
};

export const uxTestersNationalityArrayToString = (ux_testers_nationality) => {
  return ux_testers_nationality ? "[" + ux_testers_nationality.map((i) => `"${i}"`).join() + "]" : null;
};

export const figmaPrototypeUrlRegex = /^https:\/\/(?:[\w-]+\.)?figma\.com\/(?:file|proto|design)\/[0-9A-Za-z]{22,128}(?:[/?#].*)?$/;

export const allowedExternalPrototypesRegex = [
  figmaPrototypeUrlRegex,
  /^https:\/\/(\[\w\.-]+\.)?marvelapp\.com\/prototype\/([0-9a-zA-Z]+)\/?.*$/,
  /^https:\/\/([\w.-]+.)?invis\.io\/([0-9a-zA-Z]+)\/?.*$/,
  /^https:\/\/([\w.-]+.)?invisionapp\.com\/([0-9a-zA-Z]+)\/?.*$/,
];

export const isAllowedExternalPrototype = (url) => {
  let isRegexMatched = false;
  allowedExternalPrototypesRegex.forEach((r) => {
    if (url.match(r)) {
      isRegexMatched = true;
    }
  });
  return isRegexMatched;
};

export const getPrototypeIcon = (prototypeSource) => {
  let prototypeIcon = "";
  switch (Number(prototypeSource)) {
    case SOURCE_INVISION:
      prototypeIcon = "proto_invision";
      break;
    case SOURCE_FIGMA:
      prototypeIcon = "proto_figma";
      break;
    case SOURCE_MARVEL:
      prototypeIcon = "proto_marvel";
      break;
    default:
      prototypeIcon = "";
      break;
  }

  return prototypeIcon;
};

export function getPrototypeSource(url) {
  let source = null;
  if (url.includes("invis.io") || url.includes("invisionapp.com")) {
    source = SOURCE_INVISION;
  } else if (url.includes("marvelapp.com")) {
    source = SOURCE_MARVEL;
  } else if (url.includes("figma.com")) {
    source = SOURCE_FIGMA;
  }
  return source;
}

export const getProtoDeviceFrame = (is_recording_off, testing_device) =>
  !(
    Number(is_recording_off) === 0 &&
    (Number(testing_device) === DEVICE_TYPE.TABLET || Number(testing_device) === DEVICE_TYPE.MOBILE)
  );

export const getPrototypeIframeSrc = (
  url,
  source = null,
  scalingFactor = null,
  showDeviceFrame = true,
  continueURL = false,
  nodeId = ""
) => {
  if (!url) return "";
  let iframeSrc = "";

  // ------ For external prototypes which are importable via UI
  if (source === SOURCE_FIGMA) {
    // Figma
    try {
      const parsed = new URL(url);
      parsed.searchParams.delete("scaling");
      url = parsed.toString();
    } catch {
      const scalingStrings = ["fit-width", "min-zoom", "max-zoom", "scale-down-width", "scale-down", "contain"];
      scalingStrings.forEach((scale) => {
        url = url.replace(new RegExp(`[?&]scaling=${scale}`, "g"), "");
      });
    }

    iframeSrc = `${getFigmaEmbedUrl(url, scalingFactor, continueURL, nodeId)}&bg-color=ffffff&${!showDeviceFrame ? "&device-frame=false" : ""}`;
  } else if (source === SOURCE_MARVEL) {
    // Marvel
    const regex1 = /^https:\/\/(\[\w\.-]+\.)?marvelapp\.com\/prototype\/([0-9a-zA-Z]+)\/?$/;
    const regex2 = /^https:\/\/(\[\w\.-]+\.)?marvelapp\.com\/prototype\/([0-9a-zA-Z]+)\/screen\/([0-9a-zA-Z]+)\/?$/;
    if (url.match(regex1) || url.match(regex2)) {
      iframeSrc = url + "?emb=1&iosapp=false&frameless=false";
    } else {
      const splitUrl = url.split("screen");
      if (splitUrl.length > 1) {
        url = splitUrl[0].replace(/\/$/, "") + "/screen/" + splitUrl[1].replace(/\//, "");
      } else {
        url = splitUrl[0].replace(/\/$/, "");
      }
    }

    iframeSrc = url + "?emb=1&iosapp=false&frameless=false";
  } else if (source === SOURCE_INVISION) {
    // Invision
    iframeSrc = url;
  } else {
    // ------ For other prototypes which are not importable via UI
    let matches = null;
    const xdRegex = /^https:\/\/xd.adobe.com\/view\/(.*)?\/.*$/;
    if ((matches = url.match(xdRegex)) && matches.length > 1) {
      // Adobe XD
      iframeSrc = "https://xd.adobe.com/embed/" + matches[1] + "/?fullscreen&hints=off";
    }
  }

  return iframeSrc;
};

export const fixExternalPrototypeUrl = (url) => {
  if (!url) return url;

  if (getPrototypeSource(url) === SOURCE_MARVEL) {
    url = url.replace(/(\/screen\/[^/]+)(?!.*\1)/, ""); // for removing screen params from url in case of marvel
  }
  return url;
};

export const getExternalPrototypeURL = (nodeId, prototype) => {
  if (!nodeId) return null;

  const prototypeSource = prototype?.prototype_source;

  let url = null;

  prototype.url = fixExternalPrototypeUrl(prototype.url);

  switch (Number(prototypeSource)) {
    case SOURCE_FIGMA:
      const s = prototype.url.split("?");
      const protoBaseUrl = s[0];
      const protoBaseParams = s[1];
      if (protoBaseParams !== undefined) {
        const params = protoBaseParams
          .split("&")
          .reduce((r, a) => {
            let b = a.split("=");
            let str = b[0] + "=" + (b[0] === "node-id" ? encodeURIComponent(nodeId) : b[1]);
            return [...r, str];
          }, [])
          .join("&");
        url = `${protoBaseUrl}?${params}`;
      } else {
        url = prototype.url;
      }
      break;
    case SOURCE_INVISION:
      url = `${prototype.url}/#/screens/${nodeId}`;
      break;
    case SOURCE_MARVEL:
      url = `${prototype.url}/screen/${nodeId}`;
      break;
    default:
      url = null;
      break;
  }
  return url;
};

export const getEmbedUrl = (url) => {
  if (url.includes("figma"))
    return `https://figma.com/embed?embed_host=share&url=${encodeURIComponent(url)}%26scaling%3Dmini-zoom%26hide-ui%3D1`;
  else if (url.includes("marvelapp")) return `${url}?emb=1&iosapp=false&frameless=true`;
  else if (url.includes("invisionapp")) return `${url}`;
  else return url;
};

export const extractDomain = (url) => {
  // Find & remove protocol (http, ftp, etc.) and get domain
  let domain = url.split("/")[url.indexOf("://") > -1 ? 2 : 0];

  // Find & remove port number
  domain = domain.split(":")[0];

  // Remove subdomains if any
  const parts = domain.split(".");
  if (parts.length > 2) domain = parts.slice(1).join(".");

  return domain;
};

export const isSameURL = (url1, url2) => {
  const url1Info = new URL(!url1.startsWith("http") ? `http://${url1}` : url1);
  const url2Info = new URL(url2, url1Info.origin);
  return extractDomain(url1Info.href) === extractDomain(url2Info.href);
};

// Reorder the ArrayList
export const reorderArrayList = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

// Moves an item from one list to another list.
export const moveItemsBetweenList = (source, destination, droppableSource, droppableDestination) => {
  const sourceClone = Array.from(source);
  const destClone = Array.from(destination);
  const [removed] = sourceClone.splice(droppableSource.index, 1);

  destClone.splice(droppableDestination.index, 0, removed);

  const result = {};
  result[droppableSource.droppableId] = sourceClone;
  result[droppableDestination.droppableId] = destClone;

  return result;
};

// Function to remove HTML tags and keep line breaks intact - as quiiljs does
export const getTextLengthAsQuillJS = (input) => {
  if (!input) return 0;
  // Replace line breaks inside <p> and <br> tags with a special token
  const input2 = input?.replace(/(<p><br>)/g, "\n");
  const withTokens = input2.replace(/(<p>|<br>)/g, "\n");

  // Remove other HTML tags by matching everything between '<' and '>'
  const withoutTags = withTokens.replace(/<[^>]*>/g, "");

  // Restore the line breaks from the special token
  const withLineBreaks = withoutTags.replace(/\n/g, "\n");

  const doc = new DOMParser().parseFromString(withLineBreaks, "text/html");
  const textWithoutSpecialEntities = doc.documentElement.textContent;

  // Remove leading and trailing spaces and return the result
  const length = textWithoutSpecialEntities.length;
  return length === 0 ? 0 : length - 1; // -1 to remove the last line break
};

// Regular expression pattern for URL validation
export const isValidURL = (url) => {
  const urlRegex = /^(?:(?:https?|ftp):\/\/)?(?:www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:\/[^\s]*)?(\?[^\s]*)?$/;
  return urlRegex.test(url);
};

// Regular expression pattern for BLOB file urls
export const isBlobURL = (url) => {
  const blobRegex = /^blob:(http|https):\/\/[^\s]+$/;
  return blobRegex.test(url);
};

// Get Product List
export const getProductList = () => {
  const { MODERATED, UNMODERATED, CARDSORTING, TREETESTING, SURVEYS, NORECORDING } = PRODUCTS;

  return [
    {
      key: UNMODERATED,
      icon: "unmoderated",
      productLink: PATHS.V2.TBT,
      name: "Unmoderated Testing (Advanced)",
      description:
        "Test prototypes, websites and mobile apps with real screen video, webcam, voice, usability metrics, heatmaps and paths",
      type: TEST_TYPE_TASK,
      bg: "bg_unmoderated_recorded",
      color: "#547EED",
      keyFeatures: [
        "Custom task instructions and logic",
        "Full session recording (screen, audio & webcam)",
        "Support prototypes (Figma, Protopie and more), websites & mobile apps",
        "Automatically generated transcripts and translations",
        "Auto-tagging, AI summaries & sentiment analysis",
        "Highlight and Tag key moments in transcription",
        "Create and share video clips",
      ],
      bestFor: [
        "Deep-diving into complex user flows and interactions",
        "Capturing authentic reactions and verbal feedback",
        "Testing products remotely without scheduling constraints",
      ],
      youtubeVideo: "GMj2FW8bznE",
      createCTA: {
        text: "Create New Study",
        link: "/test/new",
      },
      viewCTA: {
        text: "View All Studies",
        link: PATHS.V2.TBT,
      },
      exploreDemoStudy: true,
      panelAvailable: true,
    },
    {
      key: NORECORDING,
      icon: "nav_product_icon",
      productLink: PATHS.V2.NORECORDING,
      name: "Unmoderated Testing (In-Browser)",
      description: "Test prototypes with webcam, voice, usability metrics, heatmaps and paths. No installation at participant side",
      type: TEST_TYPE_TASK, // deliberately set to TEST_TYPE_TASK
      isNoRecording: true, // TASK + No Recording => Figma
      bg: "bg_unmoderated_no_recording",
      color: "#2753C6",
      keyFeatures: [
        "Custom task instructions and logic",
        "Participant audio and webcam recording",
        "Support for Figma prototypes",
        "Analyze results with click & heat maps and path analysis",
        "Create and share video clips",
      ],
      bestFor: [
        "Testing early-stage designs before development",
        "Understanding where users click and how they navigate",
        "Quick qualitative design validation with behavioral data",
      ],
      youtubeVideo: "ukC8lONGpp8",
      createCTA: {
        text: "Create New Study",
        link: "/test/new?recording=false&survey=false",
      },
      viewCTA: {
        text: "View All Studies",
        link: PATHS.V2.NORECORDING,
      },
      panelAvailable: true,
    },
    {
      isUserInterview: true,
      key: MODERATED,
      icon: "moderated",
      productLink: PATHS.V2.MODERATED,
      name: "User Interviews",
      description: "Conduct, upload and schedule interviews with DeepDive®",
      type: TEST_TYPE_MODERATED,
      bg: "bg_moderated",
      color: "#2DCA72",
      keyFeatures: [
        "Host 1:1, dyad, triad interviews or focus groups (up to 10)",
        "Support for 2 moderators, 2 translators, 25 hidden observers",
        "Chat with participants or team privately",
        "Live notes & post-session annotations",
        "AI summaries with topic-based navigation",
        "Tag and create shareable highight reels",
        "Multi-language support for transcript translation",
      ],
      bestFor: [
        "Getting rich qualitative insights through conversation",
        "Collaborating with stakeholders through live observation",
        "Quickly extracting insights from recorded sessions with AI",
      ],
      youtubeVideo: "GF_2SSKfR4I",
      viewCTA: {
        text: "View All Studies",
        link: PATHS.V2.MODERATED,
        type: "primary",
      },
      panelAvailable: false,
    },
    {
      key: SURVEYS,
      icon: "surveys",
      productLink: PATHS.V2.SURVEYS,
      name: "Surveys",
      description: "Gather quick, quantitative user feedback",
      type: TEST_TYPE_SURVEY,
      isNoRecording: true,
      bg: "bg_survyes",
      color: "#FFC233",
      keyFeatures: [
        "Flexible question types (ranking, multiple choice, rating, etc.)",
        "Custom screeners to qualify participants",
        "Conditional logic & question branching",
        "Answer piping & carry-forward logic",
        "AI follow-ups for open ended responses",
        "Add images to questions",
        "Filter & analyze results by participant or custom parameters",
      ],
      bestFor: [
        "Collecting structured feedback at scale",
        "Measuring user preferences and opinions quantitatively",
        "Qualifying participants before deeper research",
      ],
      youtubeVideo: "KrPBqpNCxn0",
      createCTA: {
        text: "Create New Survey",
        link: "/test/new?recording=false&survey=true",
      },
      viewCTA: {
        text: "View All Surveys",
        link: PATHS.V2.SURVEYS,
      },
      panelAvailable: true,
    },
    {
      isToolkit: true,
      key: CARDSORTING,
      icon: "card_sorting",
      productLink: PATHS.V2.TOOLKIT.CARDSORTING,
      name: "Card Sorting",
      description: "Find how your users categorize and label information",
      type: TEST_TYPE_CARDSORT,
      bg: "bg_cardsorting",
      color: "#F75FDE",
      keyFeatures: [
        "Open, closed or hybrid card sorting",
        "Bulk upload cards via XLS/CSV or create manually",
        "Collect participant reasoning for card choices",
        "Analyze grouping patterns & label preferences",
        "Export results for further analysis",
      ],
      bestFor: [
        "Understanding how users naturally organize content",
        "Validating navigation structures and menu organization",
        "Testing category labels that resonate with your audience",
      ],
      youtubeVideo: "Qewy10Z8N6c",
      createCTA: {
        text: "Create New Study",
        link: "/tests/new/card-sorting",
      },
      viewCTA: {
        text: "View All Studies",
        link: PATHS.V2.TOOLKIT.CARDSORTING,
      },
      panelAvailable: false,
    },
    {
      isToolkit: true,
      key: TREETESTING,
      icon: "tree_testing",
      productLink: PATHS.V2.TOOLKIT.TREETESTING,
      name: "Tree Testing",
      description: "Evaluate usability and findability on your website or app",
      type: TEST_TYPE_TREETEST,
      bg: "bg_treetesting",
      color: "#7047EB",
      keyFeatures: [
        "Set up or import tree structure via XLS/CSV",
        "Define tasks with expected paths",
        "Track success rates & navigation paths",
        "Visualize results with pie trees or destination matrices",
        "Export results for further analysis",
      ],
      bestFor: [
        "Validating navigation structures early, before design",
        "Testing if users can find what they're looking for",
        "Pinpointing where users get confused or struggle",
      ],
      youtubeVideo: "BTTxhvBXdao",
      createCTA: {
        text: "Create New Study",
        link: "/tests/new/tree-testing",
      },
      viewCTA: {
        text: "View All Studies",
        link: PATHS.V2.TOOLKIT.TREETESTING,
      },
      panelAvailable: false,
    },
  ];
};

export const canHaveLogic = (task, showScreener = false) => {
  if (showScreener) return true;

  const { surveyType } = task || {};
  return (
    surveyType === RADIO ||
    surveyType === CHECKBOX ||
    surveyType === LIKERT_5PT ||
    surveyType === LIKERT_7PT ||
    surveyType === LIKERT_10PT ||
    surveyType === NPS
  );
};

export const isMobileUserAgent = () => {
  const userAgent = navigator.userAgent;
  const mobileUserAgentPattern =
    /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|NetFront|Silk-Accelerated|(hpw|web)OS|Fennec|Minimo|Opera M(obi|ini)|Blazer|Dolfin|Dolphin|Skyfire|Zune/;
  if (mobileUserAgentPattern.test(userAgent)) return true;
  return false;
};

// Best-effort detection for getDisplayMedia's `preferCurrentTab` option, which
// restricts screen-share to the current tab (skipping the browser's normal
// full-screen/window/other-tab picker). It's a Chromium-only, non-standard
// option with no capability-query API, so this falls back to UA sniffing.
// Verify against the current Chrome/Edge behaviour when testing.
export const supportsCurrentTabCapture = () => {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getDisplayMedia) return false;
  const brands = navigator.userAgentData?.brands;
  if (brands?.length) return brands.some((b) => /Chromium|Google Chrome|Microsoft Edge|Opera/i.test(b.brand));
  return /Chrome\/|Chromium\/|Edg\/|OPR\//.test(navigator.userAgent);
};

// Best-effort fullscreen helpers. Some browsers (notably iOS Safari) don't support the
// Fullscreen API for non-video elements, so these silently no-op where it's unavailable.
// Must be called from within a user gesture (e.g. a click handler) to succeed.
export const requestFullscreenSafe = (element = document.documentElement) => {
  if (!element) return;
  const request =
    element.requestFullscreen || element.webkitRequestFullscreen || element.webkitRequestFullScreen || element.msRequestFullscreen;
  if (!request) return;
  try {
    const result = request.call(element);
    if (result && typeof result.catch === "function") result.catch(() => {});
  } catch (e) {}
};

// Feature detection for the checks above - lets callers avoid promising fullscreen (UI copy,
// toggle buttons) on browsers like iOS Safari that don't support it for non-video elements.
export const isFullscreenApiSupported = (element = typeof document !== "undefined" ? document.documentElement : null) => {
  if (!element) return false;
  return Boolean(
    element.requestFullscreen || element.webkitRequestFullscreen || element.webkitRequestFullScreen || element.msRequestFullscreen
  );
};

export const exitFullscreenSafe = () => {
  if (typeof document === "undefined") return;
  const isFullscreen = document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement;
  if (!isFullscreen) return;
  const exit = document.exitFullscreen || document.webkitExitFullscreen || document.msExitFullscreen;
  if (!exit) return;
  try {
    const result = exit.call(document);
    if (result && typeof result.catch === "function") result.catch(() => {});
  } catch (e) {}
};

// Function to get color based on index
export const getColor = (i = 0) => {
  const COLORS = [
    "#2E62E9",
    "#FF499F",
    "#F2C94C",
    "#A871EC",
    "#F2994A",
    "#03A6A6",
    "#EB5757",
    "#BB6BD9",
    "#56CCF2",
    "#6FCF97",
    "#2D9CDB",
    "#9B51E0",
    "#607D8B",
    "#FF7D52",
    "#00ACFF",
    "#2DCA72",
    "#F75FDE",
    "#FFC233",
    "#F53D6B",
  ];

  return COLORS[i % COLORS.length];
};

// Function to convert hex to rgba
export const hexToRgb = (hex, opacity = 1) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}, ${opacity})` : null;
};

// Get Country Name
export const getCountryName = (countryCode = "", countries = []) => {
  const country = countries.find((c) => c.iso_alpha2 === countryCode);
  return country?.name || "Unknown";
};

// Get Protype Icon Info
export const getPrototypeIconInfo = (url = "") => {
  // Figma
  if (url.includes("figma")) return { icon: "proto_figma", title: "Figma" };

  // Marvel
  if (url.includes("marvelapp")) return { icon: "proto_marvel", title: "Marvel" };

  // Invision
  if (url.includes("invisionapp") || url?.includes("invis.io")) return { icon: "proto_invision", title: "Invision" };

  // adobe xd
  if (url.includes("xd.adobe.com")) return { icon: "proto_xd", title: "Adobe XD" };

  // proto.io
  if (url.includes("proto.io")) return { icon: "proto_protopie", title: "ProtoPie" };

  // Sketch
  if (url.includes("sketch")) return { icon: "proto_sketch", title: "Sketch" };

  return "";
};

export const getCurrentTime = () => Math.round(new Date().getTime() / 1000);

export const getCustomImageName = (i = 0) => `Image ${String.fromCharCode(i + 65)}`;
export const getSeverityColor = (severity) => {
  switch (severity) {
    case "success":
      return "#28a745";
    case "info":
      return "#17a2b8";
    case "warning":
      return "#fd7e14";
    case "error":
      return "#dc3545";
    case "danger":
      return "#bd2130";
    default:
      return "#008000";
  }
};

export const getFigmaNodeIdFromUrl = (url) => {
  let urlToCheck = url;
  if (!urlToCheck.startsWith("http://www." || "https://www.")) urlToCheck = `http://www.${urlToCheck}`;
  try {
    const urlObj = new URL(urlToCheck);
    let nodeID = urlObj.searchParams.get("node-id")?.replace("-", ":");
    if (!nodeID) nodeID = urlObj.searchParams.get("starting-point-node-id")?.replace("-", ":");
    return nodeID;
  } catch {
    return null;
  }
};

export const getFigmaEmbedUrl = (url, scalingFactor = "scale-down", continueURL = false, nodeId = "") => {
  if (!url) return "";

  const normalizeDashNodeId = (id) => String(id || "").replaceAll(":", "-");
  const normalizeColonNodeId = (id) => String(id || "").replaceAll("-", ":");

  const dashNodeId = normalizeDashNodeId(nodeId);
  const colonNodeId = normalizeColonNodeId(nodeId);

  // 1) Ensure embed domain (handle both figma.com and embed.figma.com inputs)
  let embedUrl = url
    .replace("https://www.figma.com", "https://embed.figma.com")
    .replace("http://www.figma.com", "https://embed.figma.com")
    .replace("www.figma.com", "embed.figma.com");

  // Helpers
  const stripNodeParams = (u) =>
    u
      .replace(/([?&])node-id=[^&]*(&?)/g, (m, p1, p2) => (p2 ? p1 : ""))
      .replace(/([?&])starting-point-node-id=[^&]*(&?)/g, (m, p1, p2) => (p2 ? p1 : ""))
      .replace(/\?&/g, "?")
      .replace(/&&/g, "&")
      .replace(/[?&]$/, "");

  // 2) Node param logic:
  // - continueURL=true => override any existing node params with our nodeId
  // - continueURL=false => keep existing node params as-is (do NOT strip)
  if (continueURL) {
    embedUrl = stripNodeParams(embedUrl);

    if (dashNodeId) {
      const joiner = embedUrl.includes("?") ? "&" : "?";
      embedUrl += `${joiner}node-id=${dashNodeId}&starting-point-node-id=${encodeURIComponent(colonNodeId)}`;
    }
  }

  // 3) Append embed params (safe; doesn't affect node-id/start)
  const joiner = embedUrl.includes("?") ? "&" : "?";
  embedUrl +=
    joiner +
    `embed-host=uxarmy` +
    `&client-id=${process.env.REACT_APP_FIGMA_CLIENT_ID}` +
    `&footer=false` +
    `&page-selector=false` +
    `&viewport-controls=false` +
    `&disable-default-keyboard-nav=false` +
    `&hide-ui=1` +
    `&scaling=${scalingFactor}`;

  embedUrl = convertToProtoUrl(embedUrl); // for embed links change /file and /design to /proto in url

  return embedUrl;
};

// Calculate Figma Path Errors
export const calculatePathErrors = (pathsArr = [], currentPathObj = {}) => {
  const { currentPathIndex = 0, currentPathIds = [] } = currentPathObj || {};

  // convert all paths to array of ids
  const pathsArrIds = pathsArr.map((path) => (JSON.parse(path?.url || "[]") || []).map((url) => getFigmaNodeIdFromUrl(url)));
  const allLastElements = pathsArrIds.map((path, i) => ({ lastElement: path[path.length - 1], pathIndex: i }));

  const errObj = { hasError: false, errorPaths: [] };

  // Validation 1: A path must have atleast two nodes
  if (currentPathIds.length <= 1) {
    errObj.errorPaths.push({
      type: "single-node",
      pathIndex: currentPathIndex,
      message: `This path(Path ${currentPathIndex + 1}) must have atleast two nodes.`,
    });
    errObj.hasError = true;
  } else {
    // Validation 2: A path must not be identical to another path
    pathsArrIds.reduce((obj, pathIds, index) => {
      if (JSON.stringify(pathIds) === JSON.stringify(currentPathIds) && index !== currentPathIndex) {
        obj.hasError = true;
        obj.errorPaths.push({
          type: "identical",
          pathIndex: index,
          conflictingPathIndex: index,
          message: `This path is identical to another path (Path ${index + 1})`,
        });
      }

      return obj;
    }, errObj);

    // Validation 3: The end screen of another path must not come in the middle of this path
    allLastElements?.reduce((obj, pathObj) => {
      // Check if an identical error already exists
      if (obj?.hasError && obj?.errorPaths?.find(({ type, pathIndex }) => type === "identical" && pathIndex === pathObj?.pathIndex)) {
        return obj;
      }

      const conflictingScreenIndex = currentPathIds?.slice(0, -1)?.indexOf(pathObj?.lastElement);
      if (conflictingScreenIndex > -1) {
        obj.hasError = true;
        obj.errorPaths.push({
          type: "conflict",
          pathIndex: pathObj?.pathIndex,
          conflictingPathIndex: pathObj?.pathIndex,
          conflictStartIndex: conflictingScreenIndex,
          message: `The end screen of another path (Path ${pathObj.pathIndex + 1}) must not come in the middle of this path (Screen ${
            conflictingScreenIndex + 1
          })`,
        });
      }

      return errObj;
    }, errObj);
  }

  return errObj;
};

// Update iframs frame to the given node id
export const changeFigmaiFrameNodeId = (iframeRef = null, nodeId = "") => {
  if (!iframeRef) return;

  // Post message to iframe
  iframeRef?.contentWindow?.postMessage({ data: { nodeId }, type: "NAVIGATE_TO_FRAME_AND_CLOSE_OVERLAYS" }, "https://www.figma.com");
};

export const resourcesLinks = [
  // { key: 1, label: "Sample Figma prototype", link: "/" },
  // { key: 2, label: "Sample images", link: "/" },
  // { key: 3, label: "Workspace guide", link: "https://help.uxarmy.com/space/general/274857985/Team+WorkSpace+Guide " },
  {
    key: "getting_started",
    label: "Getting started with workspaces",
    link: "https://help.uxarmy.com/space/general/297074689/Getting+started+with+UXArmy+workspaces",
  },
  {
    key: "setup_roles",
    label: "Setting up roles in workspaces",
    link: "https://help.uxarmy.com/space/general/297173004/Setting+up+roles+in+your+UXArmy+workspace:+A+Guide+for+Teams",
  },
  {
    key: 4,
    label: "How UXArmy's credit system works",
    link: "https://help.uxarmy.com/space/general/196902918/How+UXArmy's+Credit+System+Works ",
  },
  {
    key: 5,
    label: "Recruiting UXArmy's user panel",
    link: "https://help.uxarmy.com/space/UT/91226154/Overview+of+Inviting+Participants ",
  },
  // { key: 6, label: "Need help with research", link: "https://www.uxarmy.com/contact-sales" },
];

export const getFormattedNumber = (number) => new Intl.NumberFormat()?.format(number);

// Get Formatted Date
export const getFormattedDayjsDate = (date, format = "MMM D, YYYY") => dayjs(date).format(format);

export const roundCredits = (credits, prec = 2) => {
  return parseFloat(Number(credits).toFixed(prec));
};

export const scaleHeatmapDataPoints = (points, originalWidth, originalHeight, renderedWidth, renderedHeight) => {
  const scaleX = renderedWidth / originalWidth;
  const scaleY = renderedHeight / originalHeight;

  return points.map((p) => {
    return {
      ...p,
      x: parseInt(p.x * scaleX),
      y: parseInt(p.y * scaleY),
    };
  });
};

export const isInvalidEmail = (email) => email && !email.match(/^[\w+']+([.-][\w+']+)*@\w+([.-]\w+)*(\.\w{2,32})+$/);

// Emails Has Errors
export const emailsHasErrors = (emails = [], disallowGmail = false) => {
  if (!emails || emails.length === 0) return { error: true, errMessage: "Please fill the emails" };

  if (emails.some(isInvalidEmail)) return { error: true, errMessage: "Please remove the invalid emails." };

  // Do not allow gmail emails
  if (disallowGmail && emails.some((email) => email.includes("@gmail.com"))) {
    return { error: true, errMessage: "Please use a non-Gmail account to proceed." };
  }

  return { error: false, errMessage: "" };
};

export const normalizeUrl = (url) => {
  if (url) {
    const httpRegex = /^https?:\/\//;
    const wwwOnlyRegex = /^www\./;

    // Remove https:// or http:// if present
    url = url.replace(httpRegex, "");

    // Remove www if it's the only subdomain
    if (wwwOnlyRegex.test(url)) {
      url = url.replace(wwwOnlyRegex, "");
    }
  }
  return url;
};

export const stripHtml = (htmlString) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");
  return doc.body.textContent || "";
};

// Get Valid JSON from string
export const getValidJSON = (jsonString) => {
  // Return as it is if it's already an object
  if (typeof jsonString === "object") return jsonString;
  if (!jsonString || typeof jsonString !== "string") return null;

  try {
    const parsed = JSON.parse(jsonString);
    return typeof parsed === "object" && parsed !== null ? parsed : null;
  } catch (e) {
    console.error("Invalid JSON string:", e);
    return null;
  }
};

export function generateRedirectUrl(baseUrl, overrideParams = {}) {
  if (!baseUrl) return "";

  try {
    // Check if baseUrl starts with a scheme
    const hasScheme = /^https?:\/\//i.test(baseUrl);
    const dummyBase = "https://dummy.base"; // For relative URLs

    // Temporarily add scheme if missing for parsing
    const urlToParse = hasScheme ? baseUrl : `https://${baseUrl}`;
    const urlObj = new URL(urlToParse, dummyBase);

    urlObj.searchParams.forEach((value, key) => {
      // Check if value is like {someKey}
      const match = value.match(/^\{(.+?)\}$/);
      if (match) {
        const varKey = match[1].trim();
        if (overrideParams.hasOwnProperty(varKey)) {
          urlObj.searchParams.set(key, overrideParams[varKey]);
        }
      }
    });

    // Build final URL using the parts
    const path = urlObj.pathname;
    const search = urlObj.search;
    const hash = urlObj.hash;
    const host = urlObj.host;

    const finalUrl = hasScheme ? `${urlObj.protocol}//${host}${path}${search}${hash}` : `${host}${path}${search}${hash}`;

    return finalUrl;
  } catch (error) {
    console.error("Invalid URL passed to generateRedirectUrl:", baseUrl, error);
    return baseUrl; // fallback to original
  }
}

export function getGetParams() {
  return Object.fromEntries(new URLSearchParams(window.location.search));
}

// Remove Text Selection
export function removeTextSelection() {
  window?.getSelection()?.removeAllRanges();
}

export function ensureHttps(url) {
  if (!url || url === "") return url;

  // If URL doesn't start with http:// or https://, add https://
  if (!/^https?:\/\//i.test(url)) {
    url = "https://" + url;
  }
  return url;
}

// Get Timestamp in Milliseconds
export const getTimestampInMS = (timestamp) => (timestamp?.toString()?.length === 10 ? timestamp * 1000 : timestamp);

// Get the background color based on the first letter of the text
let nameColors = {};
export function getBackgroundColor(name) {
  const colors = ["#2DCA72", "#F75FDE", "#00ACFF", "#F53D6B", "#FF7D52", "#FFC107", "#4CAF50", "#9C27B0", "#795548"];
  const assignedColor = nameColors[name] || "";
  const color = assignedColor || colors[Math.floor(Math.random() * colors.length)];

  // Store the color
  if (!assignedColor) nameColors[name] = color;

  return color;
}

export const sanitizeString = (str) =>
  str
    .replace(/[^a-zA-Z0-9\s]/g, "") // remove special characters
    .trim() // remove extra spaces at ends
    .replace(/\s+/g, "_") // replace spaces with underscore
    .toLowerCase();

export const normalizeCreditsValue = (value) => {
  if (!value) return 0;

  const firstPart = value.split(" ")[0];
  const numericString = firstPart.replace(/,/g, "");
  return Number(numericString);
};

export const getPrototypeError = (prototype) => {
  const { status, error_details } = prototype ?? {};
  const responseHeaders = error_details?.errorObj?.responseHeaders ?? {};
  const retryAfter = Number((responseHeaders["retry-after"] ?? responseHeaders["Retry-After"] ?? responseHeaders["RETRY-AFTER"]) || 0);
  const retryAfterTime = moment().add(retryAfter, "seconds").format("DD MMM YYYY, h:mm A");

  const PROTO_STATUS_ERROR = {
    PROTO_STATUS_ERR_TIMEOUT: {
      code: -1,
      message: (
        <>
          We're still processing your Figma prototype import in the background. Please refresh the page after a couple of minutes to
          check the import status.
          <br />
          <br />
          If the import fails, consider removing unnecessary frames or splitting your Figma prototype to have fewer objects.
        </>
      ),
      hideRetryBtn: true,
      importFailedText: "File import is taking longer than expected",
    },
    PROTO_STATUS_ERR_INVISION_CURL: { code: -4, message: "Invision viewing permissions not set to public" },
    PROTO_STATUS_ERR_INVISION_INVALID_URL: { code: -5, message: "Invision Invalid Url Error" },
    PROTO_STATUS_ERR_INVISION_NO_FRAMES: { code: -6, message: "Invision No Frames Error" },
    PROTO_STATUS_ERR_INVISION_NO_CONFIG: { code: -7, message: "Invision No Config Error" },
    PROTO_STATUS_ERR_MARVEL_CURL: { code: -8, message: "Marvel viewing permissions not set to public" },
    PROTO_STATUS_ERR_MARVEL_NO_FRAMES: { code: -9, message: "Marvel No Frames Error" },
    PROTO_STATUS_ERR_MARVEL_INVALID_URL: { code: -10, message: "Marvel Invalid Url Error" },
    PROTO_STATUS_ERR_GENERIC: { code: -11, message: "Generic Error" },

    STATUS_ERR_FIGMA_400: {
      code: -2,
      message: (
        <>
          We couldn't import this Figma file. The link may be invalid, or the file may be too large. <br />
          <br />
          Please check the Figma link and try again. <br />
          <br />
          If the file is large, we recommend copying the required frames into a new Figma file, reducing image sizes, and importing the
          new file instead. <br />
        </>
      ),
    },
    STATUS_ERR_FIGMA_NO_FRAMES: {
      code: -3,
      message: (
        <>
          Figma prototypes without Frames are not supported currently. <br />
          <br />
          Frames are a best practice in Figma so we recommend including them. <br />
          <br />
          <a
            target='_blank'
            rel='noreferrer'
            className='text-underline'
            href='https://help.figma.com/hc/en-us/articles/360041539473-Frames-in-Figma-Design'
          >
            Learn how to fix this
          </a>
        </>
      ),
    },
    STATUS_ERR_FIGMA_403: {
      code: -12,
      message: (
        <>
          Access to this prototype is restricted. This usually happens if the link is invalid or you don’t have permission to view the
          file. <br />
          <br />
          Please go to{" "}
          <Link className='text-underline' to={PATHS.V2.WORKSPACE}>
            {"Settings >> Workspace >> Integration"}
          </Link>{" "}
          to reconnect your Figma account with the correct permissions.
        </>
      ),
      hideRetryBtn: true,
    },
    STATUS_ERR_FIGMA_404: {
      code: -13,
      message: (
        <>
          Please set your Figma sharing permissions to <span className='fw-600'>"Anyone"</span> with{" "}
          <span className='fw-600'>"Can View"</span> access, then try again. <br />
          <br />
          <a
            target='_blank'
            rel='noreferrer'
            className='text-underline'
            href='https://help.uxarmy.com/space/UT/89620594/Common+Figma+Prototype+Issues'
          >
            Learn how to fix this
          </a>
        </>
      ),
    },
    STATUS_ERR_FIGMA_429: {
      code: -14,
      message: (
        <>
          We couldn't add your prototype because you have exceeded your Figma user request limit. <br />
          <br />
          <ul>
            <li>Please upgrade your Figma plan to Professional</li>
            <li>Or your seat to Full or Dev</li>
            <li>Or try again after ({retryAfterTime})</li>
          </ul>
          <br />
          <br />
          <a
            target='_blank'
            rel='noreferrer'
            className='text-underline'
            href='https://help.figma.com/hc/en-us/articles/34963238552855-What-if-I-m-rate-limited'
          >
            Learn more about rate limit
          </a>
        </>
      ),
      hideRetryBtn: true,
    },
    STATUS_ERR_FIGMA_500: {
      code: -15,
      message: (
        <>
          Figma is temporarily having trouble processing this file due to file size. <br />
          <br />
          Please remove the undesired frames or consider splitting your prototype to have fewer objects.
        </>
      ),
    },
  };

  return Object.values(PROTO_STATUS_ERROR).find((e) => Number(e.code) === Number(status));
};

export const getModeratedSessionState = (state) => {
  const { ONGOING, VIDEO_PROCESSING, VIDEO_TRANSCRIBING } = DEEPDIVE_SESSION_STATE;

  if (![ONGOING, VIDEO_PROCESSING, VIDEO_TRANSCRIBING].includes(state)) return null;

  const status = {
    [ONGOING]: {
      label: "Ongoing",
      className: "bg-EEFBF4 color-17663A",
      icon: "frame_12611153747",
      thumbnail: "ic_study_ongoing",
    },
    [VIDEO_PROCESSING]: {
      label: "Processing",
      className: "bg-F0FAFF color-2E62E9",
      icon: "loading_01",
      thumbnail: "ic_study_processing",
    },
    [VIDEO_TRANSCRIBING]: {
      label: "Transcribing",
      className: "bg-F0FAFF color-2E62E9",
      icon: "file_05",
      thumbnail: null,
    },
  };

  return status[state];
};

// Get File Size in MB
export const getFileSizeInMB = (fileSizeInMb) => fileSizeInMb * 1024 * 1024;

// Which platform the recorder is running on, as RECORDING_DEVICE / RECORDING_BROWSER
// values - pass the result into getSupportedMimeType so it can prefer the container
// that platform records most reliably.
export const getRecordingPlatform = () => ({
  device: isIOS ? RECORDING_DEVICE.IOS : isAndroid ? RECORDING_DEVICE.ANDROID : RECORDING_DEVICE.DESKTOP,
  browser: isChrome ? RECORDING_BROWSER.CHROME : isSafari ? RECORDING_BROWSER.SAFARI : RECORDING_BROWSER.OTHERS,
});

// Whether this platform's MediaRecorder is WebKit's. All iOS browsers are WebKit, and
// desktop Safari shares the same MediaRecorder behaviour - which differs from Blink's
// in both the containers it can produce and how it picks a default bitrate.
export const isAppleMediaRecorder = (device = RECORDING_DEVICE.DESKTOP, browser = RECORDING_BROWSER.OTHERS) =>
  device === RECORDING_DEVICE.IOS || browser === RECORDING_BROWSER.SAFARI;

/**
 * Function to get supported MIME type for MediaRecorder.
 *
 * The single goal here is to come out of MediaRecorder with H.264 video (and
 * AAC or Opus audio), because that is the only thing the mixer can turn into a
 * universally playable MP4 by *remuxing* (stream copy, 100x+ realtime) instead
 * of re-encoding (~10-25x, i.e. minutes of CPU for a long session).
 *
 * The candidate order is therefore, for video:
 *
 *   1. MP4 / H.264 + AAC - Safari & iOS (all browsers there are WebKit), and
 *      Chrome/Edge 126+ which is when the MP4 muxer shipped in MediaRecorder.
 *   2. H.264 inside a Matroska container - Chrome/Edge below 126, which have had
 *      this since Chrome 52 on every platform with an H.264 encoder. Every real
 *      demuxer, ffmpeg included, reads it as Matroska, and the video track can
 *      be copied straight into MP4. This is what keeps older Chrome fast
 *      instead of dropping it to VP8/VP9. Requested as "video/x-matroska" rather
 *      than the equivalent "video/webm" spelling so the file we upload is not
 *      mislabelled as WebM - see the note on h264InMatroska below.
 *   3. VP9/VP8 WebM - Firefox only. It has no H.264 recording at all
 *      (bugzilla 1563140 is WONTFIX) and no MP4 (bugzilla 1631143 still open),
 *      so this is the one path where the mixer must re-encode.
 *
 * Explicit codec variants are listed after the unconstrained type so the
 * browser can pick an H.264 profile/level that suits the capture constraints,
 * while still giving a fallback for browsers that need a specific MIME type.
 */
export function getSupportedMimeType(isCamera = false, device = RECORDING_DEVICE.DESKTOP, browser = RECORDING_BROWSER.OTHERS) {
  const isApple = isAppleMediaRecorder(device, browser);

  const mp4Video = ["video/mp4", 'video/mp4;codecs="avc1,mp4a.40.2"', 'video/mp4;codecs="avc1.42E01E,mp4a.40.2"'];

  // H.264 in a Matroska container. The "video/x-matroska" spelling is deliberately
  // first: Chrome accepts both spellings for the same bytes, but the ".webm" one is a
  // lie the whole pipeline then repeats - blob.type becomes "video/webm", so the file
  // is named .webm and uploaded as Content-Type: video/webm, and WebM permits only
  // VP8/VP9/AV1. Firefox and Safari refuse to play the result. Naming it Matroska
  // costs nothing (ffmpeg uses one demuxer for both) and describes the actual file.
  const h264InMatroska = [
    "video/x-matroska;codecs=avc1,opus",
    "video/x-matroska;codecs=avc1",
    "video/webm;codecs=h264,opus",
    "video/webm;codecs=h264",
  ];

  const webmVideo = ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"];

  const mp4Audio = ["audio/mp4", "audio/mp4;codecs=mp4a.40.2"];

  const webmAudio = ["audio/webm;codecs=opus", "audio/webm"];

  let candidates;

  if (isCamera) {
    // MP4 first everywhere it exists - it needs the least work downstream and
    // is the only container that plays as-is if the mixer never runs.
    candidates = [...mp4Video, ...h264InMatroska, ...webmVideo];
  } else {
    // Audio-only: Opus in WebM and AAC in MP4 are both a cheap transcode, so
    // keep each engine on its native container.
    candidates = isApple ? [...mp4Audio, ...webmAudio] : [...webmAudio, ...mp4Audio];
  }

  return candidates.find((type) => MediaRecorder.isTypeSupported(type)) || "";
}

// File extension for a recorded blob. MediaRecorder can hand back H.264 in a
// Matroska container (see getSupportedMimeType), which is neither .mp4 nor a
// real .webm, so name it .mkv rather than mislabel it.
export const getRecordingFileExtension = (mimeType = "") => {
  const type = String(mimeType || "").toLowerCase();

  if (type.includes("mp4")) return "mp4";
  if (type.includes("matroska")) return "mkv";
  return "webm";
};

// Whether a recorded blob wants the EBML metadata rewrite below. WebM and Matroska
// are both EBML, so both can have their cues/duration moved to the front - the codec
// inside is irrelevant, since it only ever appears in CodecID/CodecPrivate/SimpleBlock
// payloads that the rewrite copies through verbatim. (H.264 was previously excluded
// for fear of corrupting it; getSeekableBlob now validates its own output and falls
// back to the original blob, so attempting it can only help.) An MP4 has no EBML at
// all - it needs the mixer's remux, not this.
export const needsSeekableFix = (mimeType = "") => {
  const type = String(mimeType || "").toLowerCase();

  if (type.includes("mp4")) return false;
  return type.includes("webm") || type.includes("matroska");
};

// Ceiling on what we will hand to a media element or read into an ArrayBuffer.
//
// MediaRecorder output declares no duration, so the only way to learn it is to make
// the browser demux the whole file (see getMediaDuration) - and the EBML rewrite has
// to read the whole file into memory too. Both cost memory proportional to the
// recording. iOS Safari kills the renderer for that on a long session and then
// silently reloads the page, which for a test in progress means the participant is
// dumped back at language selection with their session lost. A wall-clock duration or
// a merely-unseekable upload is a far better outcome than that, and the mixer repairs
// both server-side anyway - so past this size we simply do not look.
const MAX_IN_MEMORY_BLOB_BYTES = 64 * 1024 * 1024;

// Get Seekable Blob
export const getSeekableBlob = (inputBlob) => {
  const EBML = window?.EBML || null;

  return new Promise((resolve) => {
    // The rewrite below reads the entire blob into an ArrayBuffer and then builds two
    // more copies of it. Past the ceiling that is a renderer kill, not a slow path, so
    // ship the original unseekable blob and let the mixer's remux add the cues instead.
    if (inputBlob?.size > MAX_IN_MEMORY_BLOB_BYTES) {
      console.warn(`Recording is ${Math.round(inputBlob.size / (1024 * 1024))}MB - too large to rewrite in the browser.`);
      return resolve(inputBlob);
    }

    // 1. Check if library exists immediately
    if (typeof EBML === "undefined") {
      console.warn("EBML library not found. Returning original blob.");
      return resolve(inputBlob);
    }

    const { tools } = EBML;
    const reader = new EBML.Reader();
    const decoder = new EBML.Decoder();
    const fileReader = new FileReader();

    // 2. Set a safety timeout (Fail-safe)
    // If something goes wrong that even try/catch misses, resolve after 3s
    const timeout = setTimeout(() => {
      console.warn("EBML processing timed out.");
      resolve(inputBlob);
    }, 3000);

    fileReader.onload = function () {
      try {
        // This is where the "No schema entry" error usually happens
        const ebmlElms = decoder.decode(this.result);

        ebmlElms.forEach((element) => reader.read(element));

        reader.stop();

        const refinedMetadataBuf = tools.makeMetadataSeekable(reader.metadatas, reader.duration, reader.cues);

        // The rewrite can come back degenerate *without throwing* - the decoder silently
        // skips EBML elements it has no schema entry for, and we would then splice a new
        // header onto a body cut at the wrong offset and ship a file that is worse than
        // the unseekable one we started with. Sanity-check before trusting it: an empty
        // metadata buffer loses the header outright, a metadataSize of 0 duplicates it,
        // one past the end of the file truncates everything, and a zero duration means
        // the reader never really parsed the stream.
        const isUsableRewrite =
          refinedMetadataBuf?.byteLength > 0 && reader.metadataSize > 0 && reader.metadataSize < inputBlob.size && reader.duration > 0;

        if (!isUsableRewrite) {
          console.warn("EBML rewrite looks unusable. Returning original blob.", {
            metadataBytes: refinedMetadataBuf?.byteLength,
            metadataSize: reader.metadataSize,
            duration: reader.duration,
          });
          clearTimeout(timeout);
          return resolve(inputBlob);
        }

        // Cues are what make it seekable; without them the rewrite still contributes the
        // duration, which players need to render a scrubber at all, so keep it either way.
        if (!reader.cues?.length) console.warn("EBML rewrite carries no cues - duration only.");

        const body = this.result.slice(reader.metadataSize);
        // Keep the container/codec the recorder actually produced. Overriding this with
        // a hardcoded "video/webm" mislabelled every audio-only recording, and that type
        // becomes the S3 Content-Type (see uploadVideoToS3) as well as the file extension.
        const newBlob = new Blob([refinedMetadataBuf, body], { type: inputBlob?.type || "video/webm" });

        clearTimeout(timeout);
        resolve(newBlob);
      } catch (err) {
        console.error("EBML metadata injection failed:", err);
        clearTimeout(timeout);
        // Fallback to original blob so the app doesn't break
        resolve(inputBlob);
      }
    };

    fileReader.onerror = () => {
      clearTimeout(timeout);
      resolve(inputBlob);
    };

    fileReader.readAsArrayBuffer(inputBlob);
  });
};

/**
 * Pulls the finished blob out of a RecordRTCPromisesHandler that was started with
 * `timeSlice`, without losing the tail of the recording.
 *
 * RecordRTC's MediaStreamRecorder.stop() concatenates its accumulated chunks on a
 * 100ms setTimeout after calling MediaRecorder.stop(). The final partial slice
 * arrives via ondataavailable *asynchronously*, and on a loaded mobile device it
 * routinely misses that 100ms window - it lands in the recorder's chunk array but
 * after the blob has already been snapshotted, so the last few seconds are silently
 * dropped. So: wait a beat, then rebuild from the chunk array if it has outgrown the
 * blob RecordRTC handed us.
 */
export const getRecordedBlob = async (recorder, settleMs = 400) => {
  if (!recorder) return null;

  const blob = await recorder.getBlob();
  await new Promise((resolve) => setTimeout(resolve, settleMs));

  try {
    const internalRecorder = await recorder.getInternalRecorder();
    const chunks = internalRecorder?.getArrayOfBlobs?.() || [];
    const chunksSize = chunks.reduce((acc, chunk) => acc + (chunk?.size || 0), 0);

    if (chunks.length && chunksSize > (blob?.size || 0)) {
      return new Blob(chunks, { type: blob?.type || chunks[0]?.type || "video/webm" });
    }
  } catch (err) {
    console.warn("Could not re-read recorder chunks, using the blob as-is", err);
  }

  return blob;
};

/**
 * The real playable duration of a recorded blob, in seconds, or null if it can't be
 * read (in which case fall back to wall-clock elapsed time). Prefer this for anything
 * the player seeks by: wall clock counts stretches the encoder never captured
 * (backgrounded tab, a camera the OS froze), which leaves every derived timestamp
 * pointing past where that moment actually is in the file.
 *
 * Returns null without touching the media pipeline for recordings over
 * MAX_IN_MEMORY_BLOB_BYTES - see the note there.
 */
export const getMediaDuration = (blob) =>
  new Promise((resolve) => {
    if (!blob?.size) return resolve(null);

    if (blob.size > MAX_IN_MEMORY_BLOB_BYTES) {
      console.warn(`Recording is ${Math.round(blob.size / (1024 * 1024))}MB - too large to probe for duration safely.`);
      return resolve(null);
    }

    const isAudio = String(blob.type || "")
      .toLowerCase()
      .startsWith("audio");
    const element = document.createElement(isAudio ? "audio" : "video");
    const url = URL.createObjectURL(blob);
    let settled = false;

    const finish = (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      // Detach explicitly rather than waiting to be collected, so the decode buffers
      // are released before the upload starts competing for the same memory.
      try {
        element.pause();
        element.removeAttribute("src");
        element.load();
      } catch {
        /* nothing was decoded */
      }
      URL.revokeObjectURL(url);
      resolve(Number.isFinite(value) && value > 0 ? value : null);
    };

    // Fail-safe: never hold up the upload waiting on a file the decoder won't read.
    const timeout = setTimeout(() => finish(null), 5000);

    // Whichever of these first reports a real duration wins; the seek below can fire
    // timeupdate while duration is still Infinity, so keep waiting until it isn't
    // (the timeout above is what ends it if the browser never works it out).
    const onDurationKnown = () => {
      if (Number.isFinite(element.duration)) finish(element.duration);
    };

    element.addEventListener("loadedmetadata", () => {
      if (Number.isFinite(element.duration)) return finish(element.duration);

      // Duration is Infinity/NaN - seeking past the end forces the browser to scan to
      // the real end, at which point it clamps currentTime and learns the duration.
      element.addEventListener("durationchange", onDurationKnown);
      element.addEventListener("timeupdate", onDurationKnown);
      element.currentTime = 1e101;
    });
    element.addEventListener("error", () => finish(null));

    element.preload = "metadata";
    element.muted = true;
    element.src = url;
  });

// Device name (from log)
export const getLogDeviceName = (deviceOSLog, isRecordingOff) => {
  const { user, user_agent } = deviceOSLog || {};

  if (!isRecordingOff || !user_agent) return user?.device_name || "-";

  try {
    const { device } = parser(user_agent);
    return device?.type ? "Mobile" : "Desktop";
  } catch {
    return user?.device_name || "-";
  }
};

// Device type for an AI moderated interview, derived from the log's user agent.
// Unlike getLogDeviceName this isn't gated on recording being off — an AI interview
// always records, and we still need to know whether it ran on a phone (where the
// canvas recorder can't capture an embedded prototype).
export const getAiSessionDeviceType = (user_agent) => {
  if (!user_agent) return null;

  try {
    const { device } = parser(user_agent);
    return device?.type ? "Mobile" : "Computer";
  } catch {
    return null;
  }
};

// Whether an AI interview was recorded by compositing a canvas rather than by capturing
// the browser tab — in which case an embedded prototype never made it into the video.
// Mirrors the runtime decision in AiInterviewExperience (`isMobileUserAgent() ||
// !supportsCurrentTabCapture()`), but read back from the log's stored user agent:
// phones have no getDisplayMedia at all, and only Chromium-family browsers can capture
// a single tab, so Safari and Firefox fall back to the canvas too.
export const isAiSessionCanvasRecorded = (user_agent) => {
  if (!user_agent) return false;

  try {
    const { device, browser } = parser(user_agent);
    if (device?.type) return true;
    return !/^(Chrome|Chromium|Edge|Opera)/i.test(browser?.name || "");
  } catch {
    return false;
  }
};

// Browser name (from log)
export const getLogBrowserName = (deviceOSLog) => {
  const { user, user_agent } = deviceOSLog || {};

  if (!user_agent) return user?.device_name || "-";

  try {
    const { browser } = parser(user_agent);
    return browser?.name || user?.device_name || "-";
  } catch {
    return user?.device_name || "-";
  }
};

// OS name (from log)
export const getLogOSName = (deviceOSLog, isRecordingOff) => {
  const { user, user_agent } = deviceOSLog || {};

  if (!isRecordingOff || !user_agent) return user?.os;

  try {
    const { os } = parser(user_agent);
    const isAndroid = os?.name?.toLowerCase()?.includes("android");
    return os?.name ? `${os?.name} ${!isAndroid ? os?.version : ""}` : user?.os || "-";
  } catch {
    return user?.os;
  }
};

// get filter type for custom filters based on task type
export const getReportFilterType = (task) => {
  const { type, surveyType } = task || {};

  let filterType = REPORT_CUSTOM_FILTER_TYPES.NONE;

  if (
    Number(type) === TASK_TYPES.FIRSTCLICK ||
    Number(type) === TASK_TYPES.IMAGE ||
    Number(type) === TASK_TYPES.BLANK ||
    Number(type) === TASK_TYPES.FIVESEC
  ) {
    filterType = REPORT_CUSTOM_FILTER_TYPES.NONE;
  } else if (
    Number(type) === TASK_TYPES.SURVEY &&
    (surveyType === SURVEY_TYPES.NOTE || surveyType === SURVEY_TYPES.RANKING || surveyType === SURVEY_TYPES.MATRIX)
  ) {
    filterType = REPORT_CUSTOM_FILTER_TYPES.NONE;
  } else if (
    Number(type) === TASK_TYPES.DEFAULT ||
    Number(type) === TASK_TYPES.EXTERNAL_PROTOTYPE ||
    Number(type) === TASK_TYPES.MOBAPP_PROTOTYPE
  ) {
    const showTaskTime = Number(task?.criteria?.value) === SUCCESS_CRITERIA.NONE;
    filterType = showTaskTime ? REPORT_CUSTOM_FILTER_TYPES.TASK_DURATION : REPORT_CUSTOM_FILTER_TYPES.SUCCESS_CRITERIA;
  } else if (Number(type) === TASK_TYPES.SURVEY && (surveyType === SURVEY_TYPES.SIMPLE_INPUT || surveyType === SURVEY_TYPES.TEXT)) {
    filterType = REPORT_CUSTOM_FILTER_TYPES.CONTAINS;
  } else if (
    Number(type) === TASK_TYPES.SURVEY &&
    (surveyType === SURVEY_TYPES.RADIO ||
      surveyType === SURVEY_TYPES.CHECKBOX ||
      surveyType === SURVEY_TYPES.LIKERT_5PT ||
      surveyType === SURVEY_TYPES.LIKERT_7PT ||
      surveyType === SURVEY_TYPES.LIKERT_10PT ||
      surveyType === SURVEY_TYPES.NPS)
  ) {
    filterType = REPORT_CUSTOM_FILTER_TYPES.CONDITION;
  }

  return filterType;
};

export function setReportFilters(storageKey, nextFilters) {
  if (!storageKey) return;

  localStorage.setItem(storageKey, JSON.stringify(nextFilters));

  // broadcast within same tab
  window.dispatchEvent(
    new CustomEvent(REPORT_FILTERS_EVENT, {
      detail: { storageKey, filters: nextFilters },
    })
  );
}

export const downloadCustomTrackingLinks = async (customTrackingLinks, internal_name) => {
  if (!customTrackingLinks?.length) return;

  // CSV Header
  const headers = ["Sr No", "Link", "Status", "Response ID"];

  // Build rows
  const rows = customTrackingLinks.map((item, index) => {
    const status = item.log_status || item.status_label || "";
    const responseId = item.display_id ?? "-";

    return [index + 1, item.link || "", status, responseId];
  });

  downloadCsv(`Custom_links_${internal_name}.csv`, [headers, ...rows]);
};

export const detectFigmaPrototypeAccessLevel = async (prototypeUrl) => {
  if (!figmaPrototypeUrlRegex.test(prototypeUrl || "")) return null;

  const { accessLevel } = await userActions.detectFigmaAccessCheck(prototypeUrl);
  return accessLevel;
};

export const convertToProtoUrl = (url) => {
  if (!url) return url;

  return (
    url
      // Handle embed URLs
      .replace("https://embed.figma.com/design/", "https://embed.figma.com/proto/")
      .replace("https://embed.figma.com/file/", "https://embed.figma.com/proto/")

      // Handle normal URLs
      .replace("https://www.figma.com/design/", "https://www.figma.com/proto/")
      .replace("https://www.figma.com/file/", "https://www.figma.com/proto/")
  );
};

// Loop Tree Nodes
export const loopTreeNodes = (data = [], homeNodeId = "") => {
  return data.map(({ id, name, children }) => {
    const isLeaf = children.length === 0;

    return {
      id,
      key: id,
      title: name,
      isLeaf: isLeaf,
      checkable: isLeaf,
      disabled: id === homeNodeId,
      children: loopTreeNodes(children),
    };
  });
};

// Message Passing: Iframe to Parent OR Parent to Iframe
export const sendWindowPostMessage = ({ type, payload = {}, iframeRef = null }) => {
  const message = { type, payload, source: "uxarmy", timestamp: Date.now() };

  try {
    // 🔹 Native Android
    if (isAndroid && window.JSBridge?.showMessageInNative) {
      window.JSBridge.showMessageInNative(JSON.stringify(message));
      return;
    }

    // 🔹 Native iOS
    if (isIOS && window.webkit?.messageHandlers?.uxarmyHandler) {
      window.webkit.messageHandlers.uxarmyHandler.postMessage(message);
      return;
    }

    // 🔹 Parent → Iframe
    if (iframeRef?.contentWindow) {
      iframeRef.contentWindow.postMessage(message, "*");
      return;
    }

    // 🔹 Iframe → Parent OR Web fallback
    if (window?.parent && window !== window?.parent) {
      window?.parent?.postMessage(message, "*");
      return;
    }

    // 🔹 Default (same window)
    window?.postMessage(message, "*");
  } catch (err) {
    console.error("Message send failed:", err);
  }
};

// Global Message Listener
export const listenMessage = (callback) => {
  const handler = (event) => {
    const data = event?.data;

    if (!data) return;

    callback(data, event);
  };

  window.addEventListener("message", handler);

  return () => window.removeEventListener("message", handler);
};

// Get Gender Demographics Value
export const getGenderDemographicsValue = (gender) => {
  const values = { [GENDER.BOTH]: "Any", [GENDER.MALE]: "Male", [GENDER.FEMALE]: "Female" };
  return values[gender] || "";
};

export const toBool = (v) => v === true || v === 1 || v === "1" || v === "true" || v === "YES";

export const getProductObj = (testType, products) => {
  let productObj = null;
  if (testType === TEST_TYPE_TASK) {
    // TBT
    productObj = products?.find((p) => p.key === PRODUCTS.UNMODERATED);
  } else if (testType === TEST_TYPE_NORECORDING) {
    // Figma Test - No Recording
    productObj = products?.find((p) => p.key === PRODUCTS.NORECORDING);
  } else if (testType === TEST_TYPE_SURVEY) {
    // Survey
    productObj = products?.find((p) => p.key === PRODUCTS.SURVEYS);
  } else if (testType === TEST_TYPE_CARDSORT) {
    // Card Sort
    productObj = products?.find((p) => p.key === PRODUCTS.CARDSORTING);
  } else if (testType === TEST_TYPE_TREETEST) {
    // Tree Test
    productObj = products?.find((p) => p.key === PRODUCTS.TREETESTING);
  }
  return productObj;
};
